import { useAtomValue } from 'jotai';
import { useCallback, useEffect } from 'react';

import { useVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { isRejoinPromptOpenAtom } from '../../video-sdk/core/atoms';
import { RejoinError } from './useBaseRejoinModalViewModel';

export type UseRejoinModalTrackingEventsOptions = {
  /**
   * Optional appointment short ID to include in analytics events related to
   * rejoining.
   */
  appointmentShortId?: string;
};

/**
 * Tracks rejoin modal related Segment events:
 * - Dispatches an event when the rejoin modal is viewed
 * - Returns a callback to track when the user clicks to rejoin, including any
 *   error info if the rejoin fails
 */
export function useRejoinModalTrackingEvents({ appointmentShortId }: UseRejoinModalTrackingEventsOptions) {
  const isOpen = useAtomValue(isRejoinPromptOpenAtom);
  const { userClicked, userViewed } = useTrackEvent();
  const getVideoPlatform = useVideoPlatformCallback();

  const trackRejoinClicked = useCallback(
    (error?: RejoinError) => {
      userClicked(EventTypeEnum.REJOIN_SESSION_CLICKED, {
        appointmentShortId: appointmentShortId ?? '',
        errorCode: error?.name ?? '',
        source: getVideoPlatform() ?? '',
      });
    },
    [userClicked, appointmentShortId, getVideoPlatform],
  );

  useEffect(
    function dispatchViewedEventOnOpen() {
      if (isOpen) {
        userViewed(EventTypeEnum.REJOIN_SESSION_PROMPT_VIEWED, {
          appointmentShortId: appointmentShortId ?? '',
          source: getVideoPlatform() ?? '',
        });
      }
    },
    [isOpen, appointmentShortId, getVideoPlatform, userViewed],
  );

  return {
    trackRejoinClicked,
  };
}
